import React, { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography
} from '@mui/material';
import {
  StyledTableHeadCell,
  StyledTableRow,
  StyledTableCell,
  TableWrapper,
  StyledTable,
  checkboxSx,
  tableCellTypographySx
} from './DataTable.styles';

export interface Column<T> {
  id: string;
  label?: React.ReactNode;
  width?: number;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  sortValue?: (row: T) => string | number; // Custom function to extract sortable value
  type?: 'checkbox' | 'default'; // Column type: checkbox for selection column
  onClick?: (row: T) => void; // Click handler for the entire cell
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  getRowId: (row: T) => number | string;
  selected?: (number | string)[];
  onSelect?: (id: number | string) => void;
  onSelectMultiple?: (ids: (number | string)[], select: boolean) => void;
  onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  orderBy?: string;
  order?: 'asc' | 'desc';
  onSort?: (property: string) => void;
  pagination?: boolean;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
  isPdf?: boolean;
}

export const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  getRowId,
  selected = [],
  onSelect,
  onSelectMultiple,
  onSelectAll,
  orderBy,
  order,
  onSort,
  pagination = false,
  rowsPerPageOptions = [10, 25, 50, 100],
  defaultRowsPerPage = 10,
  isPdf = false
}: DataTableProps<T>) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  // Internal state for sorting when uncontrolled
  const [internalOrderBy, setInternalOrderBy] = useState<string>('');
  const [internalOrder, setInternalOrder] = useState<'asc' | 'desc'>('asc');

  // Determine effective sort values
  const isControlledSort = !!onSort;
  const effectiveOrderBy = isControlledSort ? orderBy : internalOrderBy;
  const effectiveOrder = isControlledSort ? order || 'asc' : internalOrder;

  const isSelected = (id: number | string) => selected.indexOf(id) !== -1;

  // Sort data based on effectiveOrderBy and effectiveOrder
  const sortedData = useMemo(() => {
    if (!effectiveOrderBy) return data;

    const column = columns.find((col) => col.id === effectiveOrderBy);
    if (!column || !column.sortable || column.type === 'checkbox') return data;

    return [...data].sort((a, b) => {
      // Use custom sortValue function if provided, otherwise use column.id
      const getValue = (row: T) => {
        if (column.sortValue) {
          return column.sortValue(row);
        }
        return row[column.id] ?? '';
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      // Handle different data types
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        // Convert to string for comparison
        comparison = String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      }

      return effectiveOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, effectiveOrderBy, effectiveOrder, columns]);

  // Calculate paginated data
  const paginatedData = useMemo(() => {
    const dataToPaginate = sortedData;
    if (!pagination) return dataToPaginate;
    return dataToPaginate.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedData, page, rowsPerPage, pagination]);

  // For "select all" checkbox - check if all visible rows are selected
  const visibleRowIds = useMemo(() => {
    return paginatedData.map((row) => getRowId(row));
  }, [paginatedData, getRowId]);

  const allVisibleSelected = useMemo(() => {
    if (visibleRowIds.length === 0) return false;
    return visibleRowIds.every((id) => selected.indexOf(id) !== -1);
  }, [visibleRowIds, selected]);

  const someVisibleSelected = useMemo(() => {
    const hasSomeSelected = visibleRowIds.some(
      (id) => selected.indexOf(id) !== -1
    );
    return hasSomeSelected && !allVisibleSelected;
  }, [visibleRowIds, selected, allVisibleSelected]);

  const handleSort = (property: string) => {
    if (onSort) {
      onSort(property);
    } else {
      const isAsc = effectiveOrderBy === property && effectiveOrder === 'asc';
      setInternalOrder(isAsc ? 'desc' : 'asc');
      setInternalOrderBy(property);
    }
    // Reset to first page when sorting changes
    if (pagination) {
      setPage(0);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllVisible = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // If pagination is enabled, only select/deselect visible rows
    if (pagination) {
      const shouldSelect = event.target.checked;

      if (shouldSelect) {
        // Select all visible rows that are not already selected
        const idsToSelect = visibleRowIds.filter((id) => !isSelected(id));
        if (idsToSelect.length > 0) {
          if (onSelectMultiple) {
            onSelectMultiple(idsToSelect, true);
          } else if (onSelect) {
            idsToSelect.forEach((id) => onSelect(id));
          }
        }
      } else {
        // Deselect all visible rows that are currently selected
        const idsToDeselect = visibleRowIds.filter((id) => isSelected(id));
        if (idsToDeselect.length > 0) {
          if (onSelectMultiple) {
            onSelectMultiple(idsToDeselect, false);
          } else if (onSelect) {
            idsToDeselect.forEach((id) => onSelect(id));
          }
        }
      }
    } else {
      // Original behavior when no pagination
      if (onSelectAll) {
        onSelectAll(event);
      }
    }
  };

  return (
    <TableWrapper>
      <TableContainer
        component={Paper}
        square
        style={{ boxShadow: 'none', border: 'none' }}
      >
        <StyledTable>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                if (column.type === 'checkbox') {
                  return (
                    <StyledTableHeadCell
                      key={column.id}
                      $width={column.width || 80}
                    >
                      <Checkbox
                        sx={checkboxSx}
                        indeterminate={
                          pagination
                            ? someVisibleSelected
                            : selected.length > 0 &&
                              selected.length < data.length
                        }
                        checked={
                          pagination
                            ? allVisibleSelected
                            : data.length > 0 && selected.length === data.length
                        }
                        onChange={
                          pagination ? handleSelectAllVisible : onSelectAll
                        }
                      />
                    </StyledTableHeadCell>
                  );
                }
                return (
                  <StyledTableHeadCell key={column.id} $width={column.width}>
                    {column.sortable && !isPdf ? (
                      <TableSortLabel
                        active={effectiveOrderBy === column.id}
                        direction={
                          effectiveOrderBy === column.id
                            ? effectiveOrder
                            : 'asc'
                        }
                        onClick={() => handleSort(column.id)}
                      >
                        <Typography>{column.label}</Typography>
                      </TableSortLabel>
                    ) : (
                      column.label && <Typography>{column.label}</Typography>
                    )}
                  </StyledTableHeadCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => {
              const rowId = getRowId(row);
              const isItemSelected = isSelected(rowId);
              return (
                <StyledTableRow
                  key={rowId}
                  onClick={() => onSelect && onSelect(rowId)}
                  sx={{ cursor: onSelect ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => {
                    if (column.type === 'checkbox') {
                      return (
                        <StyledTableCell
                          key={column.id}
                          padding="checkbox"
                          $width={column.width || 80}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onSelect) {
                              onSelect(rowId);
                            }
                          }}
                        >
                          <Checkbox checked={isItemSelected} sx={checkboxSx} />
                        </StyledTableCell>
                      );
                    }
                    return (
                      <StyledTableCell
                        key={column.id}
                        $width={column.width}
                        onClick={(e) => {
                          if (column.onClick) {
                            e.stopPropagation();
                            column.onClick(row);
                          }
                        }}
                        sx={{
                          cursor: column.onClick ? 'pointer' : 'default'
                        }}
                      >
                        {column.render ? (
                          column.render(row)
                        ) : (
                          <Typography sx={tableCellTypographySx}>
                            {row[column.id] ?? ''}
                          </Typography>
                        )}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </StyledTable>
      </TableContainer>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true
            }
          }}
        />
      )}
    </TableWrapper>
  );
};

